import React, { useEffect, useRef, useState, useMemo } from "react";
import Chart from "chart.js/auto";

const SchoolComparisonChart = ({
  loading = false,
  error = null,
  data = [],
}) => {
  const schoolsRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [selectedFilter, setSelectedFilter] = useState("overall");

  // Filter data based on selection
  const filteredData = useMemo(() => {
    if (!data || !data.length) return [];

    let sortedData;
    switch (selectedFilter) {
      case "completion":
        sortedData = [...data].sort(
          (a, b) => b.completionRate - a.completionRate
        );
        break;
      case "assessment":
        sortedData = [...data].sort(
          (a, b) => b.assessmentScores - a.assessmentScores
        );
        break;
      default:
        sortedData = [...data].sort(
          (a, b) => b.overallPerformance - a.overallPerformance
        );
    }

    return sortedData;
  }, [data, selectedFilter]);

  useEffect(() => {
    if (!schoolsRef.current || !filteredData || !filteredData.length) return;

    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (loading || !filteredData.length) {
      return;
    }

    // Prepare chart data
    const labels = filteredData.map((item) => item.schoolName);
    let chartData;
    let label;

    switch (selectedFilter) {
      case "completion":
        chartData = filteredData.map((item) => item.completionRate);
        label = "Completion Rate (%)";
        break;
      case "assessment":
        chartData = filteredData.map((item) => item.assessmentScores);
        label = "Assessment Scores (%)";
        break;
      default:
        chartData = filteredData.map((item) => item.overallPerformance);
        label = "Overall Performance (%)";
    }

    chartInstance.current = new Chart(schoolsRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label,
            data: chartData,
            backgroundColor: "rgba(16,185,129,0.7)",
            borderColor: "rgba(16,185,129,1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: "Percentage (%)",
            },
          },
          y: {
            title: {
              display: true,
              text: "Schools",
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top" as const,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const dataIndex = context.dataIndex;
                return [`${label}: ${context.parsed.x}%`];
              },
            },
          },
        },
      },
    });

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [filteredData, loading, selectedFilter]);

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  if (loading) {
    return (
      <div className="bg-white p-5 border border-gray-200 rounded shadow-sm">
        <div className="flex justify-between items-center mb-4 md:flex-row flex-col">
          <h3 className="text-lg font-medium text-gray-900">
            School Comparison
          </h3>
          <select className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="overall">Overall Performance</option>
            <option value="completion">Completion Rate</option>
            <option value="assessment">Assessment Scores</option>
          </select>
        </div>
        <div className="h-72 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">
              Loading school comparison...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.length) {
    return (
      <div className="bg-white p-5 border border-gray-200 rounded shadow-sm">
        <div className="flex justify-between items-center mb-4 md:flex-row flex-col">
          <h3 className="text-lg font-medium text-gray-900">
            School Comparison
          </h3>
          <select className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="overall">Overall Performance</option>
            <option value="completion">Completion Rate</option>
            <option value="assessment">Assessment Scores</option>
          </select>
        </div>
        <div className="h-72 flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 border border-gray-200 rounded shadow-sm">
      <div className="flex justify-between items-center mb-4 md:flex-row flex-col">
        <h3 className="text-lg font-medium text-gray-900">School Comparison</h3>
        <select
          value={selectedFilter}
          onChange={handleFilterChange}
          className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        >
          <option value="overall">Overall Performance</option>
          <option value="completion">Completion Rate</option>
          <option value="assessment">Assessment Scores</option>
        </select>
      </div>
      <div className="h-72">
        <canvas ref={schoolsRef}></canvas>
      </div>
    </div>
  );
};

export default SchoolComparisonChart;
